import { formatUsd } from '@ytclip/shared'
import { Crown, ExternalLink, Lock } from 'lucide-react'
import { Button } from '@/components/ui/button.tsx'
import { Card, CardBody } from '@/components/ui/card.tsx'
import { Badge, ErrorState, LoadingBlock } from '@/components/ui/feedback.tsx'
import { PageHeader } from '@/components/ui/misc.tsx'
import { useMembership } from '@/features/shared-hooks.ts'

const BENEFITS = [
  'Support prioritas langsung di grup Telegram VIP',
  'Tanya-tanya seputar YT Clip AI dan YT Short Clipper',
  'Diskusi eksklusif dengan sesama member VIP',
  'Support langsung dari developer utama soal proses Clipper YouTube',
]

export function VipSupportPage() {
  const { data, isPending, isError } = useMembership()

  return (
    <>
      <PageHeader
        title="VIP Support"
        description="Akses eksklusif ke grup Telegram VIP untuk member Gold"
      />

      {isPending ? (
        <LoadingBlock />
      ) : isError ? (
        <ErrorState message="Status membership tidak bisa dimuat." />
      ) : (
        <Card tone={data.tier === 'GOLD' ? 'primary' : 'container'}>
          <CardBody className="space-y-8 py-10">
            <div className="flex flex-col items-center gap-3 text-center">
              <span
                className={
                  data.tier === 'GOLD'
                    ? 'inline-flex size-16 items-center justify-center rounded-full bg-warning-container'
                    : 'inline-flex size-16 items-center justify-center rounded-full bg-surface-muted'
                }
              >
                {data.tier === 'GOLD' ? (
                  <Crown className="size-8 text-on-warning-container" />
                ) : (
                  <Lock className="size-8 text-on-surface-variant" />
                )}
              </span>

              <Badge tone={data.tier === 'GOLD' ? 'gold' : 'neutral'}>
                {data.tier === 'GOLD' ? 'GOLD MEMBER' : 'REGULAR'}
              </Badge>

              <p className="text-body-md text-on-surface-variant">
                Total top-up kamu:{' '}
                <span className="font-medium text-on-surface">
                  {formatUsd(data.lifetimeTopupUsd)}
                </span>
              </p>
            </div>

            {data.tier === 'GOLD' ? (
              <div className="flex flex-col items-center gap-4 text-center">
                <p className="max-w-md text-body-md text-on-surface-variant">
                  Kamu berhak mendapatkan akses ke grup Telegram VIP untuk support prioritas dan
                  diskusi eksklusif.
                </p>
                {data.groupUrl ? (
                  <a
                    href={data.groupUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-12 items-center gap-2 rounded-full bg-primary px-8 text-label-md font-medium text-on-primary transition-all duration-300 ease-emphasized hover:bg-primary/90 hover:shadow-level-2 active:scale-95"
                  >
                    Gabung grup VIP
                    <ExternalLink className="size-4" aria-hidden="true" />
                  </a>
                ) : (
                  <p className="text-label-sm text-on-surface-variant">
                    Link grup belum diatur. Hubungi admin.
                  </p>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4 text-center">
                <p className="max-w-md text-body-md text-on-surface-variant">
                  Top-up minimal{' '}
                  <span className="font-medium text-on-surface">
                    {formatUsd(data.thresholdUsd)}
                  </span>{' '}
                  untuk jadi Gold Member.
                </p>
                <p className="text-body-md text-on-surface-variant">
                  Kurang{' '}
                  <span className="font-medium text-on-surface">
                    {formatUsd(data.remainingUsd)}
                  </span>{' '}
                  lagi.
                </p>
                <ProgressBar value={data.lifetimeTopupUsd} max={data.thresholdUsd} />
                <Button variant="tonal" onClick={() => window.location.assign('/dashboard')}>
                  Top-up sekarang
                </Button>
              </div>
            )}

            <div className="border-t border-outline-variant pt-6">
              <p className="text-center text-body-md font-medium text-on-surface">
                Keuntungan Gold Member
              </p>
              <ul className="mx-auto mt-4 max-w-md space-y-2">
                {BENEFITS.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-2 text-body-md text-on-surface-variant">
                    <Crown
                      className="mt-1 size-4 shrink-0 text-on-surface-variant/60"
                      aria-hidden="true"
                    />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          </CardBody>
        </Card>
      )}
    </>
  )
}

function ProgressBar({ value, max }: { value: number; max: number }) {
  const percent = Math.min(100, Math.round((value / Math.max(max, 1)) * 100))

  return (
    <div
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Progres menuju Gold"
      className="h-2 w-full max-w-xs overflow-hidden rounded-full bg-surface-muted"
    >
      <div
        className="h-full rounded-full bg-primary transition-all duration-500 ease-emphasized"
        style={{ width: `${percent}%` }}
      />
    </div>
  )
}
